import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';

// Shared global flag accessible by non-React components (e.g. Axios api.ts)
export let globalIsOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

interface NetworkContextType {
  isOnline: boolean;
  forceOffline: boolean;
  toggleMode: () => void;
  checkStatus: () => Promise<boolean>;
}

const NetworkContext = createContext<NetworkContextType>({
  isOnline: true,
  forceOffline: false,
  toggleMode: () => {},
  checkStatus: async () => true,
});

export const useNetwork = () => useContext(NetworkContext);

export const NetworkProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [networkUp,    setNetworkUp]    = useState<boolean>(globalIsOnline);
  const [forceOffline, setForceOffline] = useState<boolean>(false);

  const isOnline = networkUp && !forceOffline;

  // Keep globalIsOnline in sync so api.ts sees the right value
  useEffect(() => {
    globalIsOnline = isOnline;
  }, [isOnline]);

  const toggleMode = useCallback(() => {
    setForceOffline(prev => {
      const next = !prev;
      globalIsOnline = networkUp && !next;
      return next;
    });
  }, [networkUp]);

  const checkStatus = useCallback(async (): Promise<boolean> => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setNetworkUp(false);
      globalIsOnline = false;
      return false;
    }

    try {
      const up = await apiService.healthCheck();
      setNetworkUp(up);
      globalIsOnline = up && !forceOffline;
      if (up && !forceOffline) {
        try {
          await apiService.syncPendingOfflineOrders();
        } catch (syncError) {
          console.warn('Offline sync attempt failed:', syncError);
        }
      }
      return up;
    } catch {
      setNetworkUp(false);
      globalIsOnline = false;
      return false;
    }
  }, [forceOffline]);

  useEffect(() => {
    const handleOnline  = () => { checkStatus(); };
    const handleOffline = () => { setNetworkUp(false); globalIsOnline = false; };

    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);

    checkStatus();
    const interval = setInterval(checkStatus, 20000);

    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [checkStatus]);

  return (
    <NetworkContext.Provider value={{ isOnline, forceOffline, toggleMode, checkStatus }}>
      {children}
    </NetworkContext.Provider>
  );
};
